/**
 * bank-paper — 「出練習卷」中心:全科自選範圍,從題庫抽題出 **A4 可列印考卷**。
 *
 * 緣由(boss 2026-06-11「我希望這些都能產出能夠列印的A4考卷」+ 鐵則「輸出永遠
 * 考卷形式不做 App 對答」):大題庫(含 LLM 擴題池)原本只有線上課程答題;本模組
 * 補上「題庫 → 紙本」的最後一塊:
 *
 *   出練習卷 → 〔自選範圍出卷(本模組)| 除法餘數智慧卷(practice-flow 舊閉環)〕
 *   自選範圍:科目 → 知識點(有題庫者)→ 題數 → 隨機抽題(避開重複)
 *     → 考卷視圖(卷頭/題號/選項/作答格)+ 🖨️ A4 列印
 *     → ✅ 寫完了,對答案 → 家長三鈕【對/粗心/不會】
 *     → recordAttemptV2 升降盒 + addFactionXP(對題數×10,合法「學習=戰力」)
 *
 * 視覺 = 學術風設計系統(pageShell / ef token);介面專業中性。
 * 學習/遊戲分離鐵則:純學習側,只動 factionXP / StudentKP / 進度,絕不碰
 * commanderXp / 戰鬥數值。
 */

import { CURRICULUM } from '../curriculum/index';
import { getBankUrl } from '../entry/bank-lookup';
import { loadBankFor, sampleGroupedPractice, isAnswerable, type EntryQuestion } from '../entry/question-source';
import { loadProgress, saveProgress, addFactionXP } from '../../shared/progress';
import { factionForSubject, FACTIONS } from '../../shared/factions';
import { findOrCreateKP, recordAttemptV2 } from '../../shared/student-kp';
import { recordMistake, deriveQuestionId } from '../../shared/mistake-log';
import type { Subject } from '../../shared/types';
import { pageShell, academicButton } from '../ui/components';
import { runPracticeFlow } from './practice-flow';
import { feedMistakesToWiki, type WikiMistakeFeed } from './wiki-bridge';

type Mark = 'correct' | 'careless' | 'wrong';

/** 每答對一題的修煉值(與 practice-flow 同一費率)。 */
const XP_PER_CORRECT = 10;

const SUBJECT_LABEL: Record<string, string> = {
  math: '數學', chinese: '國語', english: '英語', nature: '自然', social: '社會',
};
const CIRCLED = ['①', '②', '③', '④'];

/** 出練習卷入口:自選範圍出卷(主)/ 除法餘數智慧卷(舊閉環,保留)。 */
export function runPracticeCenter(container: HTMLElement, onDone: () => void): void {
  ensureBankPaperStyle();
  container.innerHTML = '';
  const shell = pageShell(container, {
    title: '出練習卷',
    subtitle: '從題庫抽新題,印成 A4 用筆寫;寫完家長對答案,熟練度與修煉值自動入帳。',
    onBack: onDone,
  });

  const wrap = document.createElement('div');
  wrap.className = 'bank-paper bp-choice';
  wrap.setAttribute('data-testid', 'practice-center');
  shell.body.appendChild(wrap);

  const pickBtn = academicButton({
    label: '📚 自選範圍出卷(全科)',
    variant: 'primary',
    onClick: () => renderSubjectPick(shell.body, onDone),
  });
  pickBtn.setAttribute('data-testid', 'practice-bank-paper');
  wrap.appendChild(pickBtn);

  const smartBtn = academicButton({
    label: '🎯 除法餘數智慧卷(依熟練度組卷)',
    variant: 'secondary',
    onClick: () => {
      container.innerHTML = '';
      runPracticeFlow(container, onDone);
    },
  });
  smartBtn.setAttribute('data-testid', 'practice-smart');
  wrap.appendChild(smartBtn);
}

/** 科目 → 有題庫的 KP 清單(依課綱章節排序,帶顯示名)。 */
export function listPaperKps(subjectId: string): { id: string; label: string; chapter: string }[] {
  const subj = CURRICULUM.find((s) => s.subject === subjectId);
  if (!subj) return [];
  const out: { id: string; label: string; chapter: string }[] = [];
  for (const ch of subj.chapters) {
    for (const kp of ch.kps) {
      if (getBankUrl(kp.id)) out.push({ id: kp.id, label: kp.label, chapter: ch.title });
    }
  }
  return out;
}

function renderSubjectPick(body: HTMLElement, onDone: () => void): void {
  body.innerHTML = '';
  const wrap = document.createElement('div');
  wrap.className = 'bank-paper bp-choice';
  wrap.setAttribute('data-testid', 'bp-subjects');
  body.appendChild(wrap);

  const head = document.createElement('p');
  head.className = 'bp-step';
  head.textContent = '選科目:';
  wrap.appendChild(head);

  for (const subj of CURRICULUM) {
    if (listPaperKps(subj.subject).length === 0) continue;
    const b = academicButton({
      label: SUBJECT_LABEL[subj.subject] ?? subj.label,
      variant: 'secondary',
      onClick: () => renderKpPick(body, subj.subject, onDone),
    });
    b.setAttribute('data-subject', subj.subject);
    wrap.appendChild(b);
  }
}

function renderKpPick(body: HTMLElement, subjectId: string, onDone: () => void): void {
  body.innerHTML = '';
  const wrap = document.createElement('div');
  wrap.className = 'bank-paper bp-kps';
  wrap.setAttribute('data-testid', 'bp-kps');
  body.appendChild(wrap);

  const head = document.createElement('p');
  head.className = 'bp-step';
  head.textContent = `選範圍(${SUBJECT_LABEL[subjectId] ?? subjectId}):`;
  wrap.appendChild(head);

  let lastChapter = '';
  for (const kp of listPaperKps(subjectId)) {
    if (kp.chapter !== lastChapter) {
      lastChapter = kp.chapter;
      const ch = document.createElement('div');
      ch.className = 'bp-chapter';
      ch.textContent = kp.chapter;
      wrap.appendChild(ch);
    }
    const b = academicButton({
      label: kp.label,
      variant: 'secondary',
      onClick: () => renderCountPick(body, subjectId, kp, onDone),
    });
    b.classList.add('bp-kp');
    b.setAttribute('data-kp', kp.id);
    wrap.appendChild(b);
  }
}

function renderCountPick(
  body: HTMLElement,
  subjectId: string,
  kp: { id: string; label: string },
  onDone: () => void,
): void {
  body.innerHTML = '';
  const wrap = document.createElement('div');
  wrap.className = 'bank-paper bp-choice';
  wrap.setAttribute('data-testid', 'bp-count');
  body.appendChild(wrap);

  const head = document.createElement('p');
  head.className = 'bp-step';
  head.textContent = `「${kp.label}」要出幾題?`;
  wrap.appendChild(head);

  for (const n of [10, 15, 20]) {
    const b = academicButton({
      label: `${n} 題`,
      variant: n === 15 ? 'primary' : 'secondary',
      onClick: () => void buildPaper(body, subjectId, kp, n, onDone),
    });
    b.setAttribute('data-count', String(n));
    wrap.appendChild(b);
  }
}

async function buildPaper(
  body: HTMLElement,
  subjectId: string,
  kp: { id: string; label: string },
  count: number,
  onDone: () => void,
): Promise<void> {
  body.innerHTML = '';
  const loading = document.createElement('p');
  loading.className = 'bp-step';
  loading.textContent = '正在抽題出卷…';
  body.appendChild(loading);

  let questions: EntryQuestion[] = [];
  try {
    // 題組安全抽題:同 group(表+承上子題)整組進整組出、保序,不切斷。
    const bank = await loadBankFor(kp.id);
    if (bank) questions = sampleGroupedPractice(bank, count, Date.now()).filter(isAnswerable);
  } catch {
    /* 載入失敗 → 下方空卷訊息 */
  }
  loading.remove();

  if (questions.length === 0) {
    const empty = document.createElement('p');
    empty.className = 'bp-step';
    empty.textContent = '這個範圍暫時抽不到題目,換一個範圍試試。';
    body.appendChild(empty);
    body.appendChild(academicButton({ label: '← 返回', variant: 'secondary', onClick: onDone }));
    return;
  }

  renderSheet(body, subjectId, kp, questions, onDone);
}

/* ── 考卷視圖(同變形卷/出練習卷版面語彙)─────────────────────────────── */

function renderSheet(
  body: HTMLElement,
  subjectId: string,
  kp: { id: string; label: string },
  questions: EntryQuestion[],
  onDone: () => void,
): void {
  body.innerHTML = '';
  const title = `${SUBJECT_LABEL[subjectId] ?? subjectId}練習卷 — ${kp.label}`;

  const root = document.createElement('div');
  root.className = 'bank-paper bp-sheet';
  root.setAttribute('data-testid', 'bp-sheet');
  body.appendChild(root);

  const toolbar = document.createElement('div');
  toolbar.className = 'bp-toolbar';
  const printBtn = academicButton({
    label: '🖨️ 列印(A4)',
    variant: 'secondary',
    onClick: () => printBankPaper(title, questions),
  });
  printBtn.setAttribute('data-testid', 'bp-print');
  const gradeBtn = academicButton({
    label: '✅ 寫完了,對答案',
    variant: 'primary',
    onClick: () => renderGrade(body, subjectId, kp, questions, onDone),
  });
  gradeBtn.setAttribute('data-testid', 'bp-grade-btn');
  toolbar.append(printBtn, gradeBtn);
  root.appendChild(toolbar);

  const header = document.createElement('div');
  header.className = 'bp-header';
  header.innerHTML =
    `<div class="bp-title">${escapeHtml(title)}</div>` +
    '<div class="bp-meta">' +
    '<span>姓名:<span class="bp-blank" style="min-width:5em"></span></span>' +
    '<span>日期:<span class="bp-blank" style="min-width:5em"></span></span>' +
    '<span>得分:<span class="bp-blank" style="min-width:3em"></span></span>' +
    '</div>';
  root.appendChild(header);

  questions.forEach((q, i) => {
    const item = document.createElement('div');
    item.className = 'bp-q';
    const prompt = document.createElement('div');
    prompt.className = 'bp-prompt';
    prompt.textContent = `${i + 1}. ${q.prompt}`;
    item.appendChild(prompt);
    if (q.kind === 'mcq') {
      const opts = document.createElement('div');
      opts.className = 'bp-options';
      opts.textContent = q.options.map((o, j) => `${CIRCLED[j] ?? `(${j + 1})`} ${o}`).join('　');
      item.appendChild(opts);
      const ans = document.createElement('div');
      ans.className = 'bp-answer';
      ans.innerHTML = '答:(　　)';
      item.appendChild(ans);
    } else {
      const ans = document.createElement('div');
      ans.className = 'bp-answer';
      ans.innerHTML = '答:<span class="bp-blank" style="min-width:8em"></span>';
      item.appendChild(ans);
    }
    root.appendChild(item);
  });
}

/* ── 🖨️ A4 列印 ──────────────────────────────────────────────────────── */

export function buildBankPaperPrintHtml(title: string, questions: EntryQuestion[]): string {
  const qs = questions
    .map((q, i) => {
      const opts =
        q.kind === 'mcq'
          ? `<div class="opts">${q.options.map((o, j) => `${CIRCLED[j] ?? `(${j + 1})`} ${escapeHtml(o)}`).join('　')}</div>`
          : '';
      const ans = q.kind === 'mcq' ? '答:(　　)' : '答:<span class="blank">&nbsp;</span>';
      return `<div class="q">
        <div class="prompt">${i + 1}. ${escapeHtml(q.prompt)}</div>
        ${opts}
        <div class="answer-line">${ans}</div>
      </div>`;
    })
    .join('');

  return `<!doctype html>
<html lang="zh-Hant">
<head>
<meta charset="utf-8">
<title>${escapeHtml(title)}</title>
<style>
  @page { size: A4 portrait; margin: 15mm; }
  body { font-family: "Songti TC", "STSong", "SimSun", "PMingLiU", serif; color: #000; line-height: 1.6; }
  .header { border-bottom: 2px solid #000; padding-bottom: 8px; margin-bottom: 16px; }
  .title { font-size: 22px; font-weight: bold; text-align: center; }
  .meta { font-size: 12px; margin-top: 8px; display: flex; flex-wrap: wrap; gap: 16px; }
  .meta .blank, .answer-line .blank { display: inline-block; min-width: 5em; border-bottom: 1px solid #000; padding: 0 4px; }
  .q { margin-bottom: 14px; page-break-inside: avoid; }
  .prompt { font-size: 14px; margin-bottom: 4px; white-space: pre-wrap; }
  .opts { font-size: 13px; margin-bottom: 4px; }
  .answer-line { font-size: 13px; }
  .answer-line .blank { min-width: 8em; }
  .print-actions { position: fixed; top: 8px; right: 8px; display: flex; gap: 8px; }
  .print-actions button { padding: 8px 16px; font-size: 14px; cursor: pointer; }
  @media print { .print-actions { display: none; } }
</style>
</head>
<body>
<div class="print-actions">
  <button onclick="window.print()">列印</button>
  <button onclick="window.close()">關閉</button>
</div>
<div class="header">
  <div class="title">${escapeHtml(title)}</div>
  <div class="meta">
    <span>姓名:<span class="blank">&nbsp;</span></span>
    <span>日期:<span class="blank">&nbsp;</span></span>
    <span>得分:<span class="blank">&nbsp;</span></span>
  </div>
</div>
${qs}
</body>
</html>`;
}

function printBankPaper(title: string, questions: EntryQuestion[]): void {
  const win = window.open('', '_blank');
  if (!win) return;
  win.document.write(buildBankPaperPrintHtml(title, questions));
  win.document.close();
  win.focus();
  win.print();
}

/* ── 家長批改 + 入帳 ─────────────────────────────────────────────────── */

function renderGrade(
  body: HTMLElement,
  subjectId: string,
  kp: { id: string; label: string },
  questions: EntryQuestion[],
  onDone: () => void,
): void {
  body.innerHTML = '';
  const root = document.createElement('div');
  root.className = 'bank-paper bp-grade';
  root.setAttribute('data-testid', 'bp-grade-screen');
  body.appendChild(root);

  const heading = document.createElement('h2');
  heading.className = 'bp-grade-title';
  heading.textContent = '家長批改 — 對答案';
  root.appendChild(heading);

  const hint = document.createElement('p');
  hint.className = 'bp-step';
  hint.textContent = '逐題看正解,幫孩子標記:【對】/【粗心】(算對但寫錯)/【不會】(觀念不懂)。沒寫的題可以不標。';
  root.appendChild(hint);

  const marks: (Mark | null)[] = questions.map(() => null);

  questions.forEach((q, idx) => {
    const card = document.createElement('div');
    card.className = 'bp-card';

    const prompt = document.createElement('div');
    prompt.className = 'bp-prompt';
    prompt.textContent = `${idx + 1}. ${q.prompt}`;
    card.appendChild(prompt);

    const answer = document.createElement('div');
    answer.className = 'bp-correct';
    const correctText = q.kind === 'mcq' ? String(q.options[q.correctAnswer] ?? q.correctAnswer) : String(q.correctAnswer);
    const lines = [`正解:${correctText}`];
    if (q.explanation) lines.push(`解析:${q.explanation}`);
    answer.textContent = lines.join('\n');
    card.appendChild(answer);

    const row = document.createElement('div');
    row.className = 'bp-marks';
    const options: { mark: Mark; label: string; variant: 'primary' | 'secondary' | 'danger' }[] = [
      { mark: 'correct', label: '對', variant: 'primary' },
      { mark: 'careless', label: '粗心', variant: 'secondary' },
      { mark: 'wrong', label: '不會', variant: 'danger' },
    ];
    const buttons: HTMLButtonElement[] = [];
    for (const opt of options) {
      const b = academicButton({ label: opt.label, variant: opt.variant, onClick: () => {} });
      b.dataset.mark = opt.mark;
      b.addEventListener('click', () => {
        marks[idx] = opt.mark;
        buttons.forEach((bb) => {
          const on = bb === b;
          bb.style.outline = on ? '2px solid var(--ef-ink)' : '';
          bb.style.outlineOffset = on ? '2px' : '';
        });
      });
      buttons.push(b);
      row.appendChild(b);
    }
    card.appendChild(row);
    root.appendChild(card);
  });

  const finish = academicButton({
    label: '完成批改',
    variant: 'primary',
    onClick: () => {
      const summary = applyGrades(subjectId, kp.id, questions, marks);
      renderFeedback(body, kp.label, summary, onDone);
    },
  });
  finish.setAttribute('data-testid', 'bp-grade-finish');
  root.appendChild(finish);
}

interface PaperSummary {
  correctCount: number;
  gradedCount: number;
  xpGained: number;
  factionName: string;
  boxBefore: number;
  boxAfter: number;
}

function applyGrades(
  subjectId: string,
  kpId: string,
  questions: EntryQuestion[],
  marks: (Mark | null)[],
): PaperSummary {
  const progress = loadProgress();
  let correctCount = 0;
  let gradedCount = 0;
  let boxBefore = 1;
  let boxAfter = 1;
  let factionName = '';
  let xpGained = 0;

  if (progress) {
    if (!Array.isArray(progress.mistakes)) progress.mistakes = []; // 舊存檔防禦(同 promote.ts)
    const kp = findOrCreateKP(progress, kpId, subjectId as Subject);
    boxBefore = kp.box;
    const nowISO = new Date().toISOString();
    const wikiFeeds: WikiMistakeFeed[] = [];
    questions.forEach((q, i) => {
      const mark = marks[i];
      if (mark == null) return;
      gradedCount += 1;
      if (mark === 'correct') correctCount += 1;
      recordAttemptV2(progress, kp, mark, nowISO, q.prompt);
      // 答錯/粗心 → 錯題本(原題重練)+ 學生 LLM Wiki 回流(R-001)。
      if (mark === 'wrong' || mark === 'careless') {
        const correctText =
          q.kind === 'mcq' ? String(q.options[q.correctAnswer] ?? q.correctAnswer) : String(q.correctAnswer);
        const qid = deriveQuestionId(q.prompt);
        recordMistake(progress, {
          questionId: qid,
          kp: kpId,
          subject: subjectId as Subject,
          prompt: q.prompt,
          correctAnswer: correctText,
          userAnswer: null,
          explanation: q.explanation || undefined,
        });
        if (mark === 'careless') {
          const entry = progress.mistakes.find((m) => m.questionId === qid);
          if (entry) entry.errorType = 'careless';
        }
        wikiFeeds.push({ subject: subjectId as Subject, kp: kpId, prompt: q.prompt, correctAnswer: correctText });
      }
    });
    feedMistakesToWiki(progress, wikiFeeds);
    boxAfter = kp.box;
    const faction = factionForSubject(subjectId as Subject);
    factionName = FACTIONS[faction].displayName;
    xpGained = correctCount * XP_PER_CORRECT;
    if (xpGained > 0) addFactionXP(progress, faction, xpGained);
    saveProgress(progress);
  } else {
    questions.forEach((_q, i) => {
      const mark = marks[i];
      if (mark == null) return;
      gradedCount += 1;
      if (mark === 'correct') correctCount += 1;
    });
  }

  return { correctCount, gradedCount, xpGained, factionName, boxBefore, boxAfter };
}

function renderFeedback(body: HTMLElement, kpLabel: string, s: PaperSummary, onDone: () => void): void {
  body.innerHTML = '';
  const root = document.createElement('div');
  root.className = 'bank-paper bp-grade';
  root.setAttribute('data-testid', 'bp-feedback');
  body.appendChild(root);

  const heading = document.createElement('h2');
  heading.className = 'bp-grade-title';
  heading.textContent = '批改完成';
  root.appendChild(heading);

  const list = document.createElement('div');
  list.className = 'bp-feedback-lines';
  const boxLine = s.boxAfter === s.boxBefore
    ? `${kpLabel} 熟練度盒 維持 box ${s.boxBefore}`
    : `${kpLabel} 熟練度盒 box ${s.boxBefore}→${s.boxAfter}`;
  const lines = [`這次對 ${s.correctCount} / ${s.gradedCount} 題`, boxLine];
  if (s.xpGained > 0) lines.push(`${s.factionName}修煉值 +${s.xpGained}`);
  list.textContent = lines.join('\n');
  list.style.whiteSpace = 'pre-wrap';
  root.appendChild(list);

  const done = academicButton({ label: '完成', variant: 'primary', onClick: onDone });
  done.style.marginTop = '12px';
  root.appendChild(done);
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const BANK_PAPER_STYLE_ID = 'ef-bank-paper-style';
function ensureBankPaperStyle(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(BANK_PAPER_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = BANK_PAPER_STYLE_ID;
  style.textContent = `
.bank-paper { font-family:var(--ef-sans); color:var(--ef-ink-soft); }
.bank-paper.bp-choice, .bank-paper.bp-kps { display:flex; flex-direction:column; gap:10px; }
.bank-paper .bp-step { margin:0 0 4px; font-size:14px; color:var(--ef-sub); line-height:1.6; }
.bank-paper .bp-chapter { font-family:var(--ef-serif); font-weight:700; font-size:14px; color:var(--ef-ink); margin:10px 0 2px; }
.bank-paper.bp-sheet { font-family:var(--ef-serif); background:var(--ef-paper-bright); border:1px solid var(--ef-line); border-radius:var(--ef-radius); box-shadow:var(--ef-shadow-card); padding:22px 24px 36px; line-height:1.7; }
.bank-paper .bp-toolbar { display:flex; gap:10px; flex-wrap:wrap; margin:0 0 18px; }
.bank-paper .bp-header { border-bottom:2px solid var(--ef-ink); padding-bottom:10px; margin-bottom:18px; }
.bank-paper .bp-title { font-family:var(--ef-serif); font-size:21px; font-weight:700; letter-spacing:1px; text-align:center; color:var(--ef-ink); margin-bottom:8px; }
.bank-paper .bp-meta { display:flex; flex-wrap:wrap; gap:16px; font-size:14px; color:var(--ef-ink-soft); }
.bank-paper .bp-blank { display:inline-block; border-bottom:1px solid var(--ef-ink); }
.bank-paper .bp-q { margin:0 0 14px; }
.bank-paper .bp-prompt { font-size:15px; color:var(--ef-ink); margin-bottom:4px; white-space:pre-wrap; }
.bank-paper .bp-options { font-size:14px; color:var(--ef-ink); margin-bottom:4px; }
.bank-paper .bp-answer { font-size:14px; color:var(--ef-ink); }
.bank-paper.bp-grade { display:flex; flex-direction:column; gap:12px; }
.bank-paper .bp-grade-title { font-family:var(--ef-serif); font-size:20px; font-weight:700; color:var(--ef-ink); margin:0; }
.bank-paper .bp-card { border:1px solid var(--ef-line); border-radius:var(--ef-radius-sm); padding:14px 16px; background:var(--ef-paper); }
.bank-paper .bp-correct { font-size:14px; color:#b3261e; border-left:3px solid #b3261e; padding-left:8px; margin:8px 0 12px; white-space:pre-wrap; }
.bank-paper .bp-marks { display:flex; gap:10px; flex-wrap:wrap; }
.bank-paper .bp-feedback-lines { font-size:15px; color:var(--ef-ink); line-height:1.9; }
`;
  document.head.appendChild(style);
}
